import React, { useState, useEffect } from "react";
import { IoClose, IoTrashOutline } from "react-icons/io5";
import { sanctionPlanEstimate } from "../../../../services/AppService";
import { formatCurrency } from "../../../../utility/formatCurrency";
import { useTranslation } from "react-i18next";

const SanctionFormModal = ({ open, onClose, planData, onSuccess }) => {
    const {t}=useTranslation();
    const [formData, setFormData] = useState({
        adminCost: "",
        adminDate: ""
    });

    // Modified to handle existing attachmentData from your API
    const [attachmentRows, setAttachmentRows] = useState([]);

    useEffect(() => {
        if (open && planData) {
            setFormData({ 
                adminCost: planData.adminCost || "", 
                adminDate: planData.adminDate || "" 
            });

            // Map the API's attachmentData to your form rows
            if (planData.attachmentData && planData.attachmentData.length > 0) {
                setAttachmentRows(planData.attachmentData.map(item => ({
                    id: item.id,
                    description: item.description || "",
                    file: null,
                    fileName: item.attachmentName || "" // Shows existing filename from API
                })));
            } else {
                // Default empty row if no attachments exist
                setAttachmentRows([{ id: Date.now(), description: "", file: null, fileName: "" }]);
            }
        }
    }, [open, planData]);

    const addRow = () => {
        setAttachmentRows([...attachmentRows, { id: Date.now(), description: "", file: null, fileName: "" }]);
    };

    const removeRow = (id) => {
        if (attachmentRows.length > 1) {
            setAttachmentRows(attachmentRows.filter(row => row.id !== id));
        }
    };

    const handleRowChange = (id, field, value) => {
        setAttachmentRows(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
    };

    const handleFileChange = (id, e) => {
        const file = e.target.files[0];
        if (file) {
            setAttachmentRows(prev => prev.map(row =>
                row.id === id ? { ...row, file: file, fileName: file.name } : row
            ));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const sanctionDto = {
            adminCost: Number(formData.adminCost),
            adminDate: formData.adminDate,
            // Track existing vs new attachments for the backend
            attachments: attachmentRows.map(row => ({
                id: row.id > 1000000000000 ? null : row.id, // Logic to identify new rows
                description: row.description.trim()
            }))
        };

        const finalFormData = new FormData();
        finalFormData.append("sanction", new Blob([JSON.stringify(sanctionDto)], { type: "application/json" }));

        attachmentRows.forEach((row) => {
            if (row.file) {
                finalFormData.append("files", row.file);
            } else {
                finalFormData.append("files", new Blob([], { type: "application/octet-stream" }));
            }
        });

        try {
            await sanctionPlanEstimate(planData.id, finalFormData);
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Sanction submission failed", error);
        }
    };

    if (!open) return null;

    return (
        <div className="staff-modal-overlay">
            <div className="staff-modal" style={{ width: '1000px', maxWidth: '95%' }}>
                <div className="staff-modal-header">
                    <div className="staff-modal-title">{t('pageTitles.addAdminSanction')}</div>
                    <button className="admin-modal-close" onClick={onClose}><IoClose /></button>
                </div>

                <div className="staff-form-body">
                    <form onSubmit={handleSubmit}>
                        {/* Prefilled Section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="form-group">
                                <label className="staff-modal-label">{t('tables.workName')}</label>
                                <input className="staff-modal-input bg-gray-100" value={planData?.nameOfWork || ""} readOnly />
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label">{t('tables.location')}</label>
                                <input className="staff-modal-input bg-gray-100" value={planData?.districtName || ""} readOnly />
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label">{t('tables.workType')}</label>
                                <input className="staff-modal-input bg-gray-100" value={planData?.workTypeName || ""} readOnly />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="form-group">
                                    <label className="staff-modal-label">{t('tables.estimateCost')}</label>
                                    <input className="staff-modal-input bg-gray-100" value={formatCurrency(planData?.estimateCost) || "0.00"} readOnly />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t('tables.date')}</label>
                                    <input className="staff-modal-input bg-gray-100" value={planData?.date || ""} readOnly />
                                </div>
                            </div>
                        </div>

                        {/* Admin Entry Section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 border rounded-lg bg-blue-50/30">
                            <div className="form-group">
                                <label className="staff-modal-label font-bold text-blue-700">{t('forms.adminSanction.sanctionedCost')} <span>*</span></label>
                                <input 
                                    type="number" 
                                    className="staff-modal-input border-blue-200" 
                                    placeholder="0.00"
                                    value={formData.adminCost}
                                    onChange={(e) => setFormData({...formData, adminCost: e.target.value})}
                                    required 
                                />
                            </div>
                            <div className="form-group">
                                <label className="staff-modal-label font-bold text-blue-700">{t('forms.adminSanction.sanctionedDate')} <span>*</span></label>
                                <input 
                                    type="date" 
                                    className="staff-modal-input border-blue-200" 
                                    value={formData.adminDate}
                                    onChange={(e) => setFormData({...formData, adminDate: e.target.value})}
                                    required 
                                />
                            </div>
                        </div>

                        {/* Attachments Section Showing API Data */}
                        <div className="flex justify-between items-center mb-2">
                            <label className="staff-modal-label m-0">{t('common.attachments')}</label>
                            <button type="button" onClick={addRow} className="staff-btn-save text-sm py-1 px-4">{t('common.addAttachment')}</button>
                        </div>

                        <table className="w-full border text-sm mb-4">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="border p-2 text-left">{t('common.description')}</th>
                                    <th className="border p-2 text-left">{t('common.attachments')}</th>
                                    <th className="border p-2 text-center w-20">{t('common.delete')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attachmentRows.map(row => (
                                    <tr key={row.id}>
                                        <td className="border p-2">
                                            <input 
                                                value={row.description} 
                                                onChange={(e) => handleRowChange(row.id, 'description', e.target.value)} 
                                                className="staff-modal-input w-full" 
                                                placeholder="Enter Description" 
                                                required 
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <div className="flex flex-col gap-1">
                                                <input type="file" onChange={(e) => handleFileChange(row.id, e)} className="text-xs" />
                                                {/* Displays existing filename from your API */}
                                                {row.fileName && <div className="text-blue-600 text-xs">{row.fileName}</div>}
                                            </div>
                                        </td>
                                        <td className="border p-2 text-center">
                                            <button type="button" onClick={() => removeRow(row.id)} className="text-red-500 hover:text-red-700">
                                                <IoTrashOutline size={20} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="flex justify-center mt-6">
                            <button type="submit" className="staff-btn-save px-12">{t('common.save')}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SanctionFormModal;